import { observer } from "mobx-react-lite";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { RefreshingBadge } from "@/components/common";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  useDashboardFiltersStore,
  useDashboardKpiStore,
} from "../hooks/useDashboardStore";
import { filterBucketsByCategory } from "../domains/category";
import ChartCardSkeleton from "./skeleton/ChartCardSkeleton";

const TOOLTIP_STYLE = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  color: "hsl(var(--foreground))",
};

const CategoryProgressChart = observer(() => {
  const { t } = useTran();
  const { summary, loading, hasLoadedOnce } = useDashboardKpiStore();
  const { filters } = useDashboardFiltersStore();

  if (!hasLoadedOnce && loading) return <ChartCardSkeleton />;

  const buckets = filterBucketsByCategory(
    summary.categories,
    new Set(filters.categories),
  );
  const data = buckets.map((c) => ({
    category: t(c.category),
    assigned: c.assigned || 0,
    actual: c.withData || 0,
    target: c.withTarget || 0,
  }));

  return (
    <div className="relative h-full">
      {hasLoadedOnce && loading && <RefreshingBadge />}
      <Card
        className={cn(
          "flex h-full w-full flex-col overflow-hidden rounded-xl border-border/70 bg-card/95 shadow-sm p-0 transition-opacity",
          hasLoadedOnce && loading && "opacity-60",
        )}
      >
        <CardHeader className="border-b px-3 py-2.5">
          <CardTitle className="text-base font-bold text-foreground">
            {t("ESG Disclosure Progress by Category")}
          </CardTitle>
          <CardDescription className="text-xs font-normal text-muted-foreground">
            {t(
              "Compare assigned, actual, and target ESG metrics across categories",
            )}{" "}
            {t(
              "Actual counts metrics with a submitted answer, including answers marked n.a; blank metrics are not counted.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data} barGap={4}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                vertical={false}
              />
              <XAxis
                dataKey="category"
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: "hsl(var(--muted))" }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12 }}
              />
              <Bar
                dataKey="assigned"
                name={t("Assigned")}
                fill="#94a3b8"
                radius={[2, 2, 0, 0]}
                barSize={24}
              />
              <Bar
                dataKey="actual"
                name={t("Actual")}
                fill="#10b981"
                radius={[2, 2, 0, 0]}
                barSize={24}
              />
              <Bar
                dataKey="target"
                name={t("Target")}
                fill="#f59e0b"
                radius={[2, 2, 0, 0]}
                barSize={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
});

export default CategoryProgressChart;
